<script lang="ts" setup>
import { PropType, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import HTMLToolbar from "./HTMLToolbar.vue";
import { Tooltip } from "@/components";
import { useFlashMessage } from "@/hooks";
import { type Config } from "./Config";

const props = defineProps({
  config: {
    type: Object as PropType<Config>,
    required: true,
  },
});

const emits = defineEmits(["reloadData", "changeState"]);

const { t } = useI18n();
const { displayFlashMessage } = useFlashMessage();

const htmlToolbarRef = ref<InstanceType<typeof HTMLToolbar>>();

type LegalNoteMode = "url" | "text";

const modeForConfig = (): LegalNoteMode =>
  props.config.ownLegalNoteUrl ? "url" : "text";

const activeMode = ref<LegalNoteMode>(modeForConfig());
const urlDraft = ref(props.config.ownLegalNoteUrl ?? "");
const textHasLocalChanges = ref(false);

const hasUnsavedChanges = computed(
  () =>
    activeMode.value !== modeForConfig() ||
    urlDraft.value !== (props.config.ownLegalNoteUrl ?? "") ||
    textHasLocalChanges.value,
);

watch(hasUnsavedChanges, (value) => {
  emits("changeState", value);
});

watch(
  () =>
    [
      props.config.id,
      props.config.ownLegalNoteUrl,
      props.config.ownLegalNote,
    ] as const,
  () => {
    activeMode.value = modeForConfig();
    urlDraft.value = props.config.ownLegalNoteUrl ?? "";
    textHasLocalChanges.value = false;
    emits("changeState", false);
  },
);

const onTextChangeState = (v: boolean) => {
  textHasLocalChanges.value = v;
};

const validHttpsUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === "https:" &&
      !!parsed.hostname &&
      !parsed.username &&
      !parsed.password
    );
  } catch {
    return false;
  }
};

const save = async () => {
  let res: { error?: any };
  if (activeMode.value === "url") {
    const url = urlDraft.value.trim() || null;

    if (url && !validHttpsUrl(url)) {
      displayFlashMessage(
        t("templateEditor.toolbar.legalNote.invalidUrl"),
        "error",
      );
      return;
    }

    res = await props.config.updateLegalNote(null, url);
  } else {
    const raw = htmlToolbarRef.value?.currentContent?.() ?? null;
    const text = raw === "<p><br></p>" ? null : raw;
    res = await props.config.updateLegalNote(text, null);
  }

  if (res.error) {
    displayFlashMessage(res.error.message, "error");
  } else {
    displayFlashMessage("Success", "success");
    emits("reloadData");
  }
};
</script>

<template>
  <div class="legal-note-toolbar">
    <div class="legal-note-body">
      <fieldset class="source-fieldset">
        <legend>
          {{ t("templateEditor.toolbar.legalNote.source.label") }}
        </legend>
        <div class="source-options">
          <label
            class="button source-option"
            :class="{ selected: activeMode === 'url' }"
          >
            <input
              v-model="activeMode"
              data-test="legal-note-source-url"
              name="legal-note-source"
              type="radio"
              value="url"
            />
            {{ t("templateEditor.toolbar.legalNote.source.url") }}
          </label>
          <label
            class="button source-option"
            :class="{ selected: activeMode === 'text' }"
          >
            <input
              v-model="activeMode"
              data-test="legal-note-source-text"
              name="legal-note-source"
              type="radio"
              value="text"
            />
            {{ t("templateEditor.toolbar.legalNote.source.text") }}
          </label>
        </div>
      </fieldset>

      <div v-show="activeMode === 'url'" class="legal-note-section">
        <label class="visually-hidden" for="legal-note-url">
          {{ t("templateEditor.toolbar.legalNote.source.url") }}
        </label>
        <div class="url-input-row">
          <input
            id="legal-note-url"
            v-model="urlDraft"
            class="input"
            data-test="legal-note-url-input"
            type="url"
            :placeholder="
              t('templateEditor.toolbar.legalNote.urlSection.placeholder')
            "
          />
          <Tooltip class="multiline legal-note-help">
            <template #default>
              <span class="material-symbols-outlined legal-note-help-icon">
                help_outline
              </span>
            </template>
            <template #tooltip>
              {{ t("templateEditor.toolbar.legalNote.urlSection.help") }}
            </template>
          </Tooltip>
        </div>
      </div>

      <div v-show="activeMode === 'text'" class="legal-note-section">
        <HTMLToolbar
          ref="htmlToolbarRef"
          :content="config.ownLegalNote ?? ''"
          :showSaveButton="false"
          editorHeight="max(300px, calc(100vh - 420px))"
          @changeState="onTextChangeState"
        />
      </div>
    </div>

    <div class="legal-note-footer">
      <button
        class="button primary"
        data-test="legal-note-save"
        type="button"
        @click="save"
      >
        {{ t("templateEditor.toolbar.save") }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.legal-note-body {
  box-sizing: border-box;
  height: calc(100vh - 60px - 60px - 82px);
  overflow-y: auto;
  padding: 1rem;
  background: var(--classic-surface, #fff);
  font-size: 0.9rem;
}
.source-fieldset {
  padding: 0;
  margin: 0 0 1rem;
  border: 0;
}
.source-fieldset legend {
  margin-bottom: 0.5rem;
  font-weight: 500;
}
.source-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}
.source-option {
  gap: 0.4rem;
  padding-inline: 0.75rem;
}
.source-option.selected {
  border-color: var(--classic-accent, #34b5ba);
}
.source-option:has(input:focus-visible) {
  outline: 2px solid var(--classic-focus, #3273dc);
  outline-offset: 2px;
}
.source-option input {
  margin: 0;
}
.legal-note-section {
  margin-bottom: 1rem;
}
.url-input-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.url-input-row .input {
  min-width: 0;
}
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.legal-note-help-icon {
  cursor: help;
  font-size: 1rem;
}
.legal-note-help.multiline :deep(.tooltip) {
  width: calc(300px - 2rem);
  max-width: calc(100vw - 2rem);
}
.legal-note-footer {
  padding: 1rem;
  background: var(--classic-surface, #fff);
  border-top: 1px solid var(--classic-border, #e0e0e0);
  font-size: 0.9rem;
}
.legal-note-section :deep(.toolbar_body) {
  height: auto !important;
  padding: 0;
  overflow-y: visible !important;
}
</style>
